import { useEffect, useState } from "react";
import { getRole } from "@/lib/auth";
import { api } from "@/lib/api";

export interface BookPermission {
  bookId: string;
  bookName: string | null;
  bookView: boolean;
  dealsView: boolean;
  bookUpload: boolean;
  manageAccess: boolean;
  dealViewIds: string[];
  /** HU-066 — this book is read under an anonymous grant. Resolved by the
   *  server; the UI only adapts its surface to it and never computes it. */
  anonymized: boolean;
}

export interface UserPermissions {
  bookId: string | null;
  bookView: boolean;
  dealsView: boolean;
  bookUpload: boolean;
  manageAccess: boolean;
  dealViewIds: string[];
  books: BookPermission[];
  isAdmin: boolean;
  loaded: boolean;
  /** True when every book this account reaches is anonymized — the
   *  third-party-guest shell. Mixed accounts use `anonymizedBookIds`. */
  anonymized: boolean;
  anonymizedBookIds: string[];
}

const ADMIN_PERMISSIONS: UserPermissions = {
  bookId: null,
  bookView: true,
  dealsView: true,
  bookUpload: true,
  manageAccess: true,
  dealViewIds: [],
  books: [],
  isAdmin: true,
  loaded: true,
  anonymized: false,
  anonymizedBookIds: [],
};

const NO_PERMISSIONS: UserPermissions = {
  bookId: null,
  bookView: false,
  dealsView: false,
  bookUpload: false,
  manageAccess: false,
  dealViewIds: [],
  books: [],
  isAdmin: false,
  loaded: true,
  anonymized: false,
  anonymizedBookIds: [],
};

let cachedPermissions: UserPermissions | null = null;

export function clearPermissionsCache() {
  cachedPermissions = null;
}

/** Synchronous accessor for non-React contexts (stores, imperative code).
 *  Returns ADMIN_PERMISSIONS for admin role; cached borrower permissions if loaded;
 *  null when the borrower permissions haven't been fetched yet. */
export function getCachedPermissions(): UserPermissions | null {
  if (getRole() === "admin") return ADMIN_PERMISSIONS;
  return cachedPermissions;
}

export function usePermissions(): UserPermissions {
  const role = getRole();
  const [perms, setPerms] = useState<UserPermissions>(
    role === "admin" ? ADMIN_PERMISSIONS : cachedPermissions ?? { ...NO_PERMISSIONS, loaded: false }
  );

  useEffect(() => {
    if (role === "admin") {
      setPerms(ADMIN_PERMISSIONS);
      return;
    }
    if (cachedPermissions) {
      setPerms(cachedPermissions);
      return;
    }
    api.getMyPermissions().then((data: {
      book_id: string;
      book_view: boolean;
      deals_view: boolean;
      book_upload: boolean;
      manage_access: boolean;
      deal_view_ids: string[];
      anonymized?: boolean;
      anonymized_book_ids?: string[];
      books?: {
        book_id: string;
        book_name: string | null;
        book_view: boolean;
        deals_view: boolean;
        book_upload: boolean;
        manage_access: boolean;
        deal_view_ids: string[];
        anonymized?: boolean;
      }[];
    }) => {
      const result: UserPermissions = {
        bookId: data.book_id,
        bookView: data.book_view,
        dealsView: data.deals_view,
        bookUpload: data.book_upload,
        manageAccess: data.manage_access ?? false,
        dealViewIds: data.deal_view_ids ?? [],
        books: (data.books ?? []).map((b) => ({
          bookId: b.book_id,
          bookName: b.book_name,
          bookView: b.book_view,
          dealsView: b.deals_view,
          bookUpload: b.book_upload,
          manageAccess: b.manage_access,
          dealViewIds: b.deal_view_ids ?? [],
          anonymized: b.anonymized ?? false,
        })),
        isAdmin: false,
        loaded: true,
        anonymized: data.anonymized ?? false,
        anonymizedBookIds: data.anonymized_book_ids ?? [],
      };
      cachedPermissions = result;
      setPerms(result);
    }).catch(() => {
      setPerms(NO_PERMISSIONS);
    });
  }, [role]);

  return perms;
}

export function hasDealsAccess(perms: UserPermissions): boolean {
  return perms.isAdmin || perms.dealsView || perms.dealViewIds.length > 0;
}

/** Whether this reader holds the Deal — the CONTRACT, not one of its
 *  facilities. `deal_view_ids` were facility ids until D4 repointed the column
 *  at `deals`; the names did not move because they stopped lying rather than
 *  started. A caller holding a facility answers with `facility.deal_id`, and
 *  the grant covers every facility of that Deal, including ones added later. */
export function canViewDeal(perms: UserPermissions, dealId: string): boolean {
  if (perms.isAdmin || perms.dealsView) return true;
  return perms.dealViewIds.includes(dealId);
}

/** HU-066 — whether this account reads the given book anonymously.
 *  With no bookId, answers for the account as a whole. The server has already
 *  redacted the payloads either way; this only decides what the shell offers. */
export function isAnonymous(perms: UserPermissions, bookId?: string | null): boolean {
  if (perms.isAdmin) return false;
  if (!bookId) return perms.anonymized;
  return perms.anonymizedBookIds.includes(bookId);
}
